import { useTranslation } from "react-i18next"

import type { UserDocument } from "@/api/models"

import { CircleX, FileText, Info, Loader2 } from "@/components/icons"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { ChatFooterFileItem } from "./chat-footer-file-item"
import {
  buildChatSelectedFileItems,
  type ChatSelectedFileItem,
  getChatSelectedFileItemLabels,
  getChatSelectedFileSummary,
} from "./chat-selected-files-utils"
import { getReadableFileType, renderFileIcon } from "./file-picker/utils"

type ChatFooterFileListProps = {
  availableFiles?: File[]
  selectedDocuments?: UserDocument[]
  filesInIndex?: string[]
  /** Session file refs (from Responses API) - always shown, even in projects */
  sessionFileRefs?: string[]
  projectId?: string
  isUploading?: boolean
  movingFileName: string | null
  /** Filename currently being removed (shows spinner) */
  removingFileName?: string | null
  onRemoveFile?: (file: File | string) => void
  onRemoveDocument?: (documentId: string) => void
  onMoveToProject: (file: File) => void
  compact?: boolean
  onOpenContextPanel?: () => void
}

export function ChatFooterFileList({
  availableFiles,
  selectedDocuments,
  filesInIndex,
  sessionFileRefs,
  projectId,
  isUploading,
  movingFileName,
  removingFileName,
  onRemoveFile,
  onRemoveDocument,
  onMoveToProject,
  compact = false,
  onOpenContextPanel,
}: ChatFooterFileListProps) {
  const { t } = useTranslation()

  const displayItems = buildChatSelectedFileItems({
    availableFiles,
    selectedDocuments,
    filesInIndex,
    sessionFileRefs,
    projectId,
    labels: getChatSelectedFileItemLabels(t),
  })
  const hasSessionFilesInProject =
    projectId && displayItems.some((item) => item.kind === "session" || item.kind === "file")

  if (displayItems.length === 0) return null

  if (compact) {
    const { visibleItems, overflowCount } = getChatSelectedFileSummary(displayItems, 2)

    return (
      <section
        className="mb-2 flex w-full max-w-[calc(100vw-50px)] items-center gap-2 px-1"
        data-testid="chat-selected-files-summary"
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {visibleItems.map((item) => (
            <CompactSelectedFileChip
              key={item.key}
              item={item}
              onOpenContextPanel={onOpenContextPanel}
            />
          ))}
          {overflowCount > 0 ? (
            <button
              className="bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground h-7 shrink-0 rounded-md px-2 text-xs font-medium transition-colors"
              type="button"
              onClick={onOpenContextPanel}
            >
              {t("questioninput.more_files", {
                count: overflowCount,
                defaultValue: "+{{count}} files",
              })}
            </button>
          ) : null}
        </div>
      </section>
    )
  }

  return (
    <section className="mb-3 flex w-full max-w-[calc(100vw-50px)] cursor-auto flex-col gap-1">
      {hasSessionFilesInProject && (
        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <span>{t("questioninput.session_files")}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{t("questioninput.session_files_info")}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
      <div
        className={cn(
          "flex gap-3 overflow-x-auto pb-2",
          hasSessionFilesInProject ? "items-center pt-3" : "-mt-3 items-start pt-3"
        )}
      >
        {displayItems.map((item, index) => {
          if (item.kind === "document") {
            const document = item.document
            if (!document) return null
            return (
              <section
                key={`${item.kind}-${document.id}-${index}`}
                className="group bg-card hover:bg-accent/50 relative shrink-0 rounded-lg border px-3 py-2 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <div className="shrink-0">
                    <FileText className="text-muted-foreground mr-1 inline size-4" />
                  </div>
                  <div className="max-w-[160px] min-w-0">
                    <div className="text-foreground truncate text-xs font-medium">
                      {document.title ||
                        t("documents.untitled", { defaultValue: "Untitled document" })}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-[10px]">
                      {t("filepicker.documents", { defaultValue: "Documents" })}
                    </div>
                  </div>
                </div>
                <button
                  className="bg-card ring-border hover:bg-destructive/10 hover:text-destructive focus-visible:ring-ring absolute -top-2.5 -right-1.5 flex size-6 cursor-pointer items-center justify-center rounded-full opacity-100 shadow-sm ring-1 transition-[color,background-color,box-shadow,scale] duration-150 hover:scale-110 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none active:scale-95 lg:opacity-0 lg:group-hover:opacity-100"
                  type="button"
                  onClick={() => onRemoveDocument?.(document.id)}
                >
                  <span className="sr-only">
                    {t("documents.remove", { defaultValue: "Remove document" })}
                  </span>
                  <CircleX className="size-3.5" />
                </button>
              </section>
            )
          }

          if (item.kind === "file") {
            const file = item.file
            if (!file) return null
            return (
              <ChatFooterFileItem
                key={`${file.name}-${index}`}
                file={file}
                isMoving={movingFileName === file.name}
                isUploading={!!isUploading}
                projectId={projectId}
                onMoveToProject={() => void onMoveToProject(file)}
                onRemove={() => onRemoveFile?.(file)}
              />
            )
          }

          // Both "indexed" and "session" kinds render the same way
          const filename = item.label
          const isRemoving = removingFileName === filename
          return (
            <section
              key={`${item.kind}-${filename}-${index}`}
              className={cn(
                "group bg-card hover:bg-accent/50 relative shrink-0 rounded-lg border px-3 py-2 transition-colors",
                isRemoving && "opacity-50"
              )}
            >
              <div className="flex items-center gap-2.5">
                <div className="shrink-0">{renderFileIcon(filename)}</div>
                <div className="max-w-[140px] min-w-0">
                  <div className="text-foreground truncate text-xs font-medium">{filename}</div>
                  <div className="text-muted-foreground mt-0.5 text-[10px]">
                    {getReadableFileType(filename)}
                  </div>
                </div>
              </div>
              {isRemoving ? (
                <div className="bg-card ring-border absolute -top-2.5 -right-1.5 flex size-6 items-center justify-center rounded-full shadow-sm ring-1">
                  <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
                </div>
              ) : (
                <button
                  className="bg-card ring-border hover:bg-destructive/10 hover:text-destructive focus-visible:ring-ring absolute -top-2.5 -right-1.5 flex size-6 cursor-pointer items-center justify-center rounded-full opacity-100 shadow-sm ring-1 transition-[color,background-color,box-shadow,scale] duration-150 hover:scale-110 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none active:scale-95 lg:opacity-0 lg:group-hover:opacity-100"
                  type="button"
                  onClick={() => onRemoveFile?.(filename)}
                >
                  <span className="sr-only">{t("questioninput.remove_file")}</span>
                  <CircleX className="size-3.5" />
                </button>
              )}
            </section>
          )
        })}
      </div>
    </section>
  )
}

function CompactSelectedFileChip({
  item,
  onOpenContextPanel,
}: {
  item: ChatSelectedFileItem
  onOpenContextPanel?: () => void
}) {
  const icon =
    item.kind === "document" ? (
      <FileText aria-hidden className="text-muted-foreground mr-1 inline size-4" />
    ) : (
      renderFileIcon(item.label, item.file?.type)
    )

  return (
    <button
      className="bg-muted text-foreground hover:bg-accent hover:text-accent-foreground flex h-7 max-w-[10rem] min-w-0 items-center gap-1 rounded-md px-2 text-xs transition-colors sm:max-w-[12rem]"
      title={item.label}
      type="button"
      onClick={onOpenContextPanel}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0 truncate">{item.label}</span>
    </button>
  )
}
